import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AppContextService, EcaSecurityService, EcaSetupConfigurationService, SetupConfigAttribute, WindowService } from '@drc-eca/eca-components-lib';
import { PortalService } from '../../core/portal.service';
import { environment } from '../../../environments/environment';
import { OktaService } from '../../core/services/okta.service';
import { LoginMessageService } from '../../core/services/login-message.service';
import { combineLatest, from, Observable, of, Subject } from 'rxjs';
import { tap, map, switchMap, catchError, takeUntil } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-product-login',
  templateUrl: './product-login.component.html',
  styleUrls: ['./product-login.component.scss']
})
export class ProductLoginComponent implements OnInit, OnDestroy {

  timeOutMessage: string = 'To ensure your privacy, you must log in again.';
  productCode$: Observable<string>;
  attributes$: Observable<any>;
  useOkta$: Observable<boolean>;
  formSubmitted$: Subject<any> = new Subject();
  errorMsg: string = '';
  isLoading = false;
  logo$: Observable<string | undefined>;
  loggedIn$: any;
  flexDirection$: any;
  destroyed = new Subject<boolean>();

  elements = ['intro', 'about', 'help'];
  oldLoginPage = !environment.useNewLoginPage;

  constructor(
    private appContextService: AppContextService,
    private portalService: PortalService,
    private route: ActivatedRoute,
    private router: Router,
    private windowService: WindowService,
    private oktaService: OktaService,
    public loginMessageService: LoginMessageService,
    private securityService: EcaSecurityService,
    private setupConfigService: EcaSetupConfigurationService
  ) {}

  async ngOnInit() {
    if (environment.useNewLoginPage) {
      const productCode = this.route.snapshot.params.productCode;
      const returnUrl = this.route.snapshot.queryParams.returnUrl;

      const params: string[] = [];
      if (productCode) {
        params.push(`c:${productCode}`);
        this.appContextService.setSelectedProductCode(productCode);
      }
      if (returnUrl) {
        // Note that this line will cause returnUrl to be double encoded. This is intentional and required.
        params.push(`returnUrl:${encodeURIComponent(returnUrl)}`);
      }

      const portalUrl = this.portalService.getPortalUrl(window.location);

      let relayState = `${portalUrl}/#/saml-login/{token}`;
      if (params.length) {
        relayState += `/${JSON.stringify(params)}`;
      }
      relayState = encodeURIComponent(relayState);

      const errorMessage = this.loginMessageService['_errorMessage'].getValue();

      const redirectUrl = `${environment.okta.samlRedirectUrl}?${productCode ? `c=${productCode}&` : ''}${errorMessage ? `e=${errorMessage}&` : ''}RelayState=${relayState}`;
      await this.oktaService.logout();
      this.windowService.navigateTo(redirectUrl);
    } else {
      const timeout = this.route.snapshot.queryParams['timeout'];
      if (timeout === 'true') {
        this.timeOutMessage = 'To ensure your privacy, you must log in again.';
      } else {
        this.timeOutMessage = '';
      }

      this.productCode$ = this.route.params.pipe(
        takeUntil(this.destroyed),
        map((params: Params) => params.productCode),
        tap((productCode) => {
          this.appContextService.setSelectedProductCode(productCode ? productCode : '');
          this.appContextService.setSelectedApplicationId(0);
        })
      );

      this.logo$ = this.productCode$.pipe(
        map(code => code && code.toUpperCase() === 'DEOCS' ? '' : undefined)
      );

      this.flexDirection$ = this.productCode$.pipe(
        map(code => code ? 'column' : 'row')
      );

      this.attributes$ = this.productCode$.pipe(
        switchMap(productCode => {
          if (productCode) {
            return this.setupConfigService.getAttributesForClient(productCode);
          } else {
            return of([{
              attribute: {
                key: 'login-method'
              },
              value: 'eca'
            }] as SetupConfigAttribute[]);
          }
        }),

        // Map array of attribute objects to a HashMap of key=value
        map(attrs => attrs.reduce((acc, attr) => (acc[attr.attribute.key] = attr.value, acc), {})),
        catchError((error) => {
          if (error.status === 404) {
            this.router.navigate(['not-found']);
            return of({});
          }
          return of({});
        })
      );

      this.useOkta$ = this.attributes$.pipe(
        map(attributes => attributes['login-method'] === 'okta')
      );

      // using a reference for this pipe helps with testing
      this.loggedIn$ = combineLatest([this.useOkta$, this.formSubmitted$]).pipe(
        switchMap(([useOkta, credentials]) => {
          this.isLoading = true;
          this.loginMessageService.clearErrorMessage();
          this.oktaService.logout();
          if (useOkta) {
            return from(this.oktaLogin(credentials)).pipe(catchError(err => {
              // Could be invalid credentials or non existent user
              if (err.errorCode === 'E0000004') {
                return this.ecaLogin(credentials).pipe(catchError(err => {
                  return this.setErrorState(err);
                }));
              } else {
                return this.setErrorState(err);
              }
            }));
          } else {
            return this.ecaLogin(credentials).pipe(catchError(err => {
              return this.setErrorState(err);
            }));
          }
        })
      );
    }
  }

  ngOnDestroy() {
    this.destroyed.next(true);
    this.destroyed.complete();
  }

  /* This method checks the HTTP error code and sets the display error message based on that */
  private setErrorState(error: HttpErrorResponse) {
    let message = 'Username or Password incorrect';

    if (error.status === 404) {
      this.router.navigate(['/error/404'], { queryParams: { desc: 'Login service not found' } });
    }
    if (error.error && error.error.code) {
      const code = error.error.code;
      switch (code) {
        case 801:
          message = 'Your account has been inactivated. Please contact your Survey Administrator or OPA to reactivate your account.';
          break;
        case 802:
          message = 'Your account has been inactivated. Please have your Point of Contact call DRC Customer Service on your behalf to request reactivation.';
          break;
        case 803:
          break;  // do nothing - authentication failed
        case 804:
          message = error.error.message;
          break;
        case 805:
          message = error.error.message;
          break;
        case 806:
          break;
        case 'island':
          message = error.error.message;
          break;
        default:
          if (error.status === 403) {
            this.router.navigate(['/error/403'], { queryParams: { desc: 'Unauthorized' } });
          } else {
            this.router.navigate(['/error/503'], { queryParams: { desc: 'There was a problem contacting the login service' } });
          }
          break;
      }
    } else if (error.status === 403) {
      this.router.navigate(['/error/403'], { queryParams: { desc: 'Unauthorized' } });
    } else {
      this.router.navigate(['/error/503'], { queryParams: { desc: 'There was a problem contacting the login service' } });
    }

    this.loginMessageService.setErrorMessage(message);
    this.isLoading = false;
    return of(message);
  }

  private async oktaLogin(credentials) {
    const transaction = await this.oktaService.signIn(credentials);
    if (transaction.status === 'SUCCESS') {

      const params: string[] = [];

      // Store the product code so that it can be restored in saml-login.component.ts later
      const productCode = await this.appContextService.getSelectedProductCode().toPromise();
      // sessionStorage.setItem('saml-login-product', productCode);
      if (productCode) {
        params.push(`c:${productCode}`);
        this.appContextService.setSelectedProductCode(productCode);
      }

      // If there is a return URL, store that too
      const returnUrl = this.route.snapshot.queryParams['returnUrl'];
      if (returnUrl) {
        // sessionStorage.setItem('saml-login-redirect-url', returnUrl);
        params.push(`returnUrl:${encodeURIComponent(returnUrl)}`);
      }

      const portalUrl = this.portalService.getPortalUrl(window.location);

      let relayState = `${portalUrl}/#/saml-login/{token}`;
      if (params.length) {
        relayState += `/${JSON.stringify(params)}`;
      }
      relayState = encodeURIComponent(relayState);

      const redirectUrl = `${environment.okta.samlRedirectUrl}?RelayState=${relayState}`;

      this.loginMessageService.clearErrorMessage();
      return this.oktaService.setCookieAndRedirect(transaction.sessionToken as string, redirectUrl);
    } else {
      throw new Error('We cannot handle the ' + transaction.status + ' status');
    }
  }

  private ecaLogin({ username, password }) {
    return this.securityService.authenticateCredentials(username, password).pipe(
      tap(userData => {
        this.appContextService.setPortalUrl(this.portalService.getPortalUrl(location));
        this.appContextService.setToken(userData.token);

        const returnUrl = this.route.snapshot.queryParams['returnUrl'];
        const productCode = this.route.snapshot.params.productCode;
        this.loginMessageService.clearErrorMessage();

        if (productCode) {
          if (returnUrl) {
            this.router.navigate([`/products/${productCode}/program-selection`], {
              queryParams: {
                returnUrl: returnUrl
              }
            });
          } else {
            this.router.navigate([`/products/${productCode}/program-selection`]);
          }
        } else {
          if (returnUrl) {
            this.router.navigate(['/products'], { queryParams: { returnUrl: returnUrl } });
          } else {
            this.router.navigate(['/products']);
          }
        }
      })
    );
  }

  onSubmitForm(credentials) {
    this.formSubmitted$.next(credentials);
  }
}
